import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import type { ComplianceScore } from '@/lib/api/types';
import { epicKeys } from './use-epics';
import { epicRequestKeys } from './use-epic-requests';

interface AuditResponse {
  data: ComplianceScore;
}

/**
 * Mutation hook to trigger a compliance audit on an epic request.
 * POST /compliance/audit/epic-requests/:id
 * Invalidates epic request queries on success.
 */
export function useAuditEpicRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.post<AuditResponse>(`/compliance/audit/epic-requests/${id}`, {}),
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: epicRequestKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: epicRequestKeys.detail(id) });
    },
  });
}

/**
 * Mutation hook to trigger a compliance audit on an epic.
 * POST /compliance/audit/epics/:id
 * Invalidates epic queries on success.
 */
export function useAuditEpic() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.post<AuditResponse>(`/compliance/audit/epics/${id}`, {}),
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: epicKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: epicKeys.detail(id) });
    },
  });
}
